'use client';

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { usePathname } from 'next/navigation';
import {
  DARK_THEME_IDS,
  THEME_STORAGE_KEY,
  THEMES,
  isDarkTheme,
  isThemePreference,
  resolveTheme,
  type ThemeId,
  type ThemeMeta,
  type ThemePreference,
} from '@/lib/theme/themes';
import {
  FIXED_LIGHT_EXACT_PATHS,
  FIXED_LIGHT_PREFIXES,
  isFixedLightRoute,
} from '@/lib/theme/public-routes';

interface ThemeContextValue {
  /** The user's stored Light/Dark preference. */
  preference: ThemePreference;
  /** The concrete theme currently applied to <html>. */
  theme: ThemeId;
  /** All selectable themes, for building a picker. */
  themes: readonly ThemeMeta[];
  setTheme: (preference: ThemePreference) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

function prefersDark(): boolean {
  return typeof window !== 'undefined'
    && typeof window.matchMedia === 'function'
    && window.matchMedia('(prefers-color-scheme: dark)').matches;
}

function readStoredPreference(): ThemePreference {
  if (typeof window === 'undefined') return 'light';
  try {
    const raw = window.localStorage.getItem(THEME_STORAGE_KEY);
    return isThemePreference(raw) ? raw : 'light';
  } catch {
    return 'light';
  }
}

/**
 * Applies a concrete theme to <html>: sets [data-theme] and keeps the legacy
 * `.dark` class in sync so existing Tailwind `dark:` utilities keep working.
 */
function applyTheme(theme: ThemeId) {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  root.setAttribute('data-theme', theme);
  root.classList.toggle('dark', isDarkTheme(theme));
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  // Lazily seeded from storage so the first application effect already sees
  // the real preference (the no-FOUC inline script has applied the correct
  // attribute before paint; this syncs React state with it).
  const [preference, setPreference] = useState<ThemePreference>(
    () => readStoredPreference(),
  );
  const [theme, setResolvedTheme] = useState<ThemeId>('light');

  const onFixedLightRoute = isFixedLightRoute(pathname);

  /**
   * Single owner of theme application. Authentication routes stay pinned to
   * light; guest commerce and the rest of the public product resolve the
   * stored preference the same way.
   * Runs on route changes too, so navigating between surfaces re-applies each
   * side's correct theme.
   */
  useEffect(() => {
    if (onFixedLightRoute) {
      setResolvedTheme('light');
      applyTheme('light');
      return;
    }
    const resolved = resolveTheme(preference, prefersDark());
    setResolvedTheme(resolved);
    applyTheme(resolved);
  }, [onFixedLightRoute, preference]);

  const setTheme = useCallback((next: ThemePreference) => {
    // Persist only; application is owned by the effect above, which enforces
    // the public light boundary automatically on whichever route is active.
    setPreference(next);
    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, next);
    } catch {
      // Ignore storage failures (private mode, etc.).
    }
  }, []);

  const value = useMemo<ThemeContextValue>(
    () => ({ preference, theme, themes: THEMES, setTheme }),
    [preference, theme, setTheme],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return ctx;
}

/**
 * Serialized script string applied in <head> before paint to avoid a
 * flash of the wrong theme (FOUC). Reads the stored preference and sets the
 * [data-theme] attribute + `.dark` class synchronously.
 *
 * Public routes are resolved to light BEFORE the stored preference is read,
 * so a visitor with a saved dark preference (or an OS dark setting) never
 * sees a dark flash on marketing, auth, menu, or transaction pages. The
 * prefix/exact lists are serialized from `lib/theme/public-routes.ts` so the
 * script can never drift from the provider's boundary.
 */
export const THEME_NO_FLASH_SCRIPT = `(function(){try{
var k=${JSON.stringify(THEME_STORAGE_KEY)};
var dark=${JSON.stringify(DARK_THEME_IDS)};
var exact=${JSON.stringify(FIXED_LIGHT_EXACT_PATHS)};
var prefixes=${JSON.stringify(FIXED_LIGHT_PREFIXES)};
var p=location.pathname;
if(p.length>1&&p.charAt(p.length-1)==='/'){p=p.slice(0,-1);}
var pub=false;
for(var i=0;i<exact.length;i++){if(p===exact[i]){pub=true;break;}}
if(!pub){for(var j=0;j<prefixes.length;j++){if(p===prefixes[j]||p.indexOf(prefixes[j]+'/')===0){pub=true;break;}}}
var t=pub?'light':(localStorage.getItem(k)||'light');
if(t!=='light'&&t!=='dark'){t='light';}
var e=document.documentElement;
e.setAttribute('data-theme',t);
if(dark.indexOf(t)>-1){e.classList.add('dark');}else{e.classList.remove('dark');}
var c=t==='dark'?'#0B1D16':'#F1F7F2';
var m=document.querySelector('meta[name="theme-color"]');
if(m){m.setAttribute('content',c);}else{m=document.createElement('meta');m.setAttribute('name','theme-color');m.setAttribute('content',c);document.head.appendChild(m);}
}catch(_){}})();`;
